import React, { useContext, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { joinDeliveryWaitlist, onDeliveryWaitlist } from "../../apollo/server";
import UserContext from "../../context/User";
import { SuggestRestaurantDialog } from "../Catering/SuggestRestaurant";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, RADIUS } from "../../utils/designTokens";

const ON_WAITLIST = gql`${onDeliveryWaitlist}`;
const JOIN = gql`${joinDeliveryWaitlist}`;

/**
 * "Want delivery in Stoneham?": join the delivery waitlist for this address,
 * or suggest a restaurant. Both are saved for the team (admin → Delivery
 * Requests) and need a signed-in customer.
 */
function DeliveryWaitlist({ city, location, onMessage }) {
  const navigate = useNavigate();
  const { isLoggedIn } = useContext(UserContext);
  const [joinedNow, setJoinedNow] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [error, setError] = useState("");
  const point =
    location?.latitude && location?.longitude ? { latitude: Number(location.latitude), longitude: Number(location.longitude) } : null;

  const { data } = useQuery(ON_WAITLIST, { variables: point, skip: !isLoggedIn || !point, fetchPolicy: "cache-and-network" });
  const [join, { loading }] = useMutation(JOIN, {
    onCompleted: () => {
      setJoinedNow(true);
      setError("");
    },
    onError: (e) => setError(e.graphQLErrors?.[0]?.message || "Couldn't save that. Please try again."),
  });
  const onList = joinedNow || data?.onDeliveryWaitlist === true;
  const place = city || "your area";

  const onNotify = () => {
    if (!isLoggedIn) return navigate("/login");
    if (!point) return undefined;
    return join({ variables: { ...point, address: location?.deliveryAddress || null, city: city || null } });
  };

  return (
    <Box component="section" aria-labelledby="waitlist-title" sx={{ maxWidth: 720, p: { xs: 2.5, sm: 3 }, borderRadius: RADIUS.card, bgcolor: C.surface }}>
      <Box component="h2" id="waitlist-title" sx={{ m: 0, mb: 1.5, fontFamily: FONT_BODY, fontSize: 17, fontWeight: 800, color: C.ink }}>
        Want delivery in {place}?
      </Box>
      <Box aria-live="polite">
        {onList ? (
          <Box sx={{ minHeight: 48, borderRadius: RADIUS.pill, bgcolor: C.greenSoft, color: C.green, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, px: 2, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700 }}>
            <CheckRoundedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
            You're on the list. We'll let you know.
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={onNotify}
            disabled={loading || !point}
            aria-busy={loading}
            sx={{
              ...BUTTON_RESET,
              ...FOCUS_RING,
              width: "100%",
              minHeight: 48,
              borderRadius: RADIUS.pill,
              border: `1.5px solid ${C.red}`,
              color: C.red,
              bgcolor: C.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 2,
              fontFamily: FONT_BODY,
              fontSize: 15,
              fontWeight: 700,
              "&:hover:not(:disabled)": { bgcolor: C.redTint },
              "&:disabled": { cursor: "default", opacity: loading ? 1 : 0.5 },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              <>
                <NotificationsNoneRoundedIcon aria-hidden="true" sx={{ fontSize: 20 }} />
                Notify me when delivery starts
              </>
            )}
          </Box>
        )}
        {error && (
          <Box component="p" role="alert" sx={{ m: 0, mt: 1, textAlign: "center", fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.red }}>
            {error}
          </Box>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setSuggestOpen(true)}
          sx={{ ...BUTTON_RESET, ...FOCUS_RING, minHeight: 44, px: 1, borderRadius: "10px", fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, color: C.redLink, "&:hover": { textDecoration: "underline" } }}
        >
          Suggest a restaurant in {place}
        </Box>
      </Box>
      <SuggestRestaurantDialog
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        city={city}
        intro={city ? `Which restaurant in ${city} should we add?` : "Which restaurant should we add?"}
        onMessage={onMessage}
      />
    </Box>
  );
}

export default DeliveryWaitlist;
